import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  compose,
  setDisplayName,
} from 'recompose';
import { get, noop } from 'lodash';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import { withStyles } from '@material-ui/core/styles';

import immutableToJS from 'helpers/immutableToJS';
import { dispatchRestMaker, includesSearchText, toCurrency } from 'helpers/utils';
import {
  ConfirmAction,
  injectIntl,
  Paper,
} from 'components';
import memberActions, { reinviteMember } from 'redux/members/actions';
import buyerActions from 'redux/buyers/actions';
import sellerActions from 'redux/sellers/actions';

import { selectState } from 'redux/selectors';

import OrganizationLoginMemberListItem from './components/OrganizationLoginMemberListItem';
import OrganizationMemberDialog from './components/OrganizationMemberDialog';

const styles = theme => ({
  button: {
    backgroundColor: '#4695ec',
    borderRadius: 5,
    marginBottom: 10,
  },
  buttonLabel: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 500,
  },
  badgeRoot: {
    marginBottom: 5,
  },
  notificationBadge: {
    backgroundColor: '#ff602e',
  },
  notificationBadgeDesk: {
    backgroundColor: '#ff602e',
    right: -30,
  },
  tab: { backgroundColor: '#ffffff' },
  tabIndicator: { backgroundColor: '#5b98ed' },
  tabLabel: {
    [theme.breakpoints.down('xs')]: {
      fontSize: 10,
    },
    fontWeight: 600,
    color: '#5b98ed',
  },
  tabsContainer: {
    borderRadius: 5,
    boxShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 6px, rgba(0, 0, 0, 0.12) 0px 1px 4px',
  },
});

class Members extends Component {
  static contextTypes = {
    currentUser: PropTypes.shape({}),
    globals: PropTypes.shape({}),
    isDistributor: PropTypes.bool,
    isFarmer: PropTypes.bool,
    isMember: PropTypes.bool,
    isRestaurant: PropTypes.bool,
    refreshToken: PropTypes.func,
  };

  static propTypes = {
    classes: PropTypes.shape({}).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func,
    }).isRequired,
    members: PropTypes.arrayOf(
      PropTypes.shape({}),
    ).isRequired,
    permissions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
    })),
    buyers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ).isRequired,
    sellers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ).isRequired,
    membersRequesting: PropTypes.bool.isRequired,
    searchText: PropTypes.string,

    createMember: PropTypes.func.isRequired,
    formatMessage: PropTypes.func.isRequired,
    listMembers: PropTypes.func.isRequired,
    removeMember: PropTypes.func.isRequired,
    updateMember: PropTypes.func.isRequired,
    reinviteMember: PropTypes.func.isRequired,
    listBuyers: PropTypes.func.isRequired,
    listSellers: PropTypes.func.isRequired,
  };

  static defaultProps = {
    permissions: [],
    searchText: '',
  };

  state = {
    currentMember: {},
    open: false,
    roleMenuItems: [],
  };

  componentWillMount() {
    const { listMembers, listBuyers, listSellers } = this.props;
    listMembers();
    listBuyers();
    listSellers();
    this.loadRoleMenuItems(this.props);
  }

  componentWillReceiveProps(nextProps) {
    const { permissions } = nextProps;
    if (this.permissions !== permissions && permissions) {
      this.loadRoleMenuItems(nextProps);
    }
  }

  onCreateNewOrg = () => {
    this.handleOpen();
  }

  createItem = (data) => {
    const { refreshToken } = this.context;
    const { createMember } = this.props;
    createMember({
      ...data,
      onSuccess: refreshToken,
    });
    this.handleClose();
  }

  handleClose = () => {
    this.setState({ open: false, currentMember: {} });
  };

  handleDelete = (data) => {
    const { removeMember } = this.props;
    removeMember(data.id);
    this.setState({ currentMember: {} });
  }

  handleOpen = (currentMember) => {
    if (!currentMember) {
      this.setState({ open: true, currentMember });
    } else {
      this.setState({ open: true, currentMember });
    }
  };

  goToCredit = () => {
    this.props.history.push('/payments');
  };

  loadRoleMenuItems = ({ permissions }) => {
    const { isFarmer, isDistributor, isRestaurant } = this.context;
    const { formatMessage } = this.props;
    const menuItems = [];
    if (!permissions || !permissions.length) {
      return this.setState({ roleMenuItems: menuItems });
    }

    const adminPermissions = permissions
      .filter(({ inBuyer, inDistributor, inSeller, isDivider }) =>
        !isDivider &&
        (isFarmer ? inSeller : isDistributor ? inDistributor : inBuyer),
      );
    menuItems.push({ title: 'Admin', subtitle: 'Access to all features', role: 'admin', permissions: adminPermissions });

    const teamMemberPermissions = permissions
      .filter(({ forAdmin, inBuyer, inDistributor, inSeller, isDivider }) =>
        !forAdmin &&
        !isDivider &&
        (isFarmer ? inSeller : isDistributor ? inDistributor : inBuyer),
      );
    const teamMemberSubtitle = teamMemberPermissions.map(({ name }) => formatMessage(name)).join(', ');
    menuItems.push({ title: 'Member', subtitle: teamMemberSubtitle, role: 'team-member', permissions: teamMemberPermissions });

    if (isFarmer) {
      const storeManagerPermissions = permissions.filter(({ name }) => name === 'Storefront');
      menuItems.push({ title: 'Permission to Update Storefront', subtitle: 'Accesses Storefront', role: 'store-manager', permissions: storeManagerPermissions });
    } 

    if (isRestaurant) {
      const orderManagerPermissions = permissions.filter(({ name }) => name === 'Order');
      menuItems.push({ title: 'Permission to Place Order', subtitle: 'Access Order', role: 'order-manager', permissions: orderManagerPermissions });
    }

    return this.setState({ roleMenuItems: menuItems });
  }

  updateItem = (data, memberId) => {
    const { refreshToken } = this.context;
    const { updateMember } = this.props;
    const { currentMember } = this.state;
    updateMember(memberId || currentMember.id, { ...data, onSuccess: refreshToken });
    this.handleClose();
  }

  getBlockInfo = (memberEmail) => {
    const { currentUser } = this.context;
    const { buyers, sellers } = this.props;

    const blockInfo = {
      store: { isBlocked: false, blockedBy: '' },
      order: { isBlocked: false, blockedBy: '' },
    };

    const blockedBuyers = buyers.filter(x =>
      x.sellerProfile.email === currentUser.organizations[0].account.email && x.isDeleted === true);
    blockedBuyers.forEach((contact) => {
      if (contact.buyerProfile.email === memberEmail) {
        const isBlockedByCustomer = contact.requestorId === contact.buyerId;
        const isBlockedVendor = contact.requestorId === contact.sellerId;
        blockInfo.store.isBlocked = true;
        blockInfo.store.blockedBy = isBlockedByCustomer === true ? 'Customer' : isBlockedVendor === true ? 'Vendor' : '';
        return blockInfo;
      }
    });

    const blockedSellers = sellers.filter(x =>
      x.buyerProfile.email === currentUser.organizations[0].account.email && x.isDeleted === true);
    blockedSellers.forEach((contact) => {
      if (contact.sellerProfile.email === memberEmail) {
        const isBlockedByCustomer = contact.requestorId === contact.buyerId;
        const isBlockedVendor = contact.requestorId === contact.sellerId;
        blockInfo.order.isBlocked = true;
        blockInfo.order.blockedBy = isBlockedByCustomer === true ? 'Customer' : isBlockedVendor === true ? 'Vendor' : '';
        return blockInfo;
      }
    });

    return blockInfo;
  }

  render() {
    const { currentUser, globals, isMember } = this.context;
    const {
      members,
      membersRequesting,
      permissions,
      classes,
      formatMessage,
      reinviteMember,
      searchText,
    } = this.props;
    const { open, currentMember, roleMenuItems } = this.state;

    const sortedMembers = members.filter(m => includesSearchText(searchText, [
      m.email,
      `${m.profile.firstName} ${m.profile.lastName}`,
    ])).sort((a, b) =>
      `${a.profile.firstName} ${a.profile.lastName}`.localeCompare(`${b.profile.firstName} ${b.profile.lastName}`),
    );

    const filteredMembers = sortedMembers.map((_item) => {
      const filteredMemberItem = Object.assign({}, _item);
      filteredMemberItem.blockInfo = _item.memberAccount && this.getBlockInfo(_item.memberAccount.email);
      return filteredMemberItem;
    });

    const currentMembers = filteredMembers.filter(member =>
      !member.accessRequest && !member.pending);

    let organizationMemberDialogTitle;
    let organizationMemberDialogSubmitButtonTitle; // From Niko: sorry for the long name :)
    if (currentMember) {
      organizationMemberDialogTitle = formatMessage('Update Company Member');
      organizationMemberDialogSubmitButtonTitle = undefined;
    } else {
      organizationMemberDialogTitle = formatMessage('Add Team Member');
      organizationMemberDialogSubmitButtonTitle = formatMessage('Save & Invite Member');
    }
    const balance = get(currentUser, 'account.creditBalance');
    const addMemberButton = (onClick = noop) => (
      <Button
        classes={{
          label: classes.buttonLabel,
          root: classes.button,
        }}
        onClick={onClick}
        variant="contained"
      >
        {formatMessage('Add Member')}
      </Button>
    );
    const needsPurchaseCredits = globals.teamMemberLimit <= currentMembers.length &&
      balance < globals.teamMemberFee;

    return (
      <Fragment>
        <div className="add-member col-sm-6">
          <div>
            {
              !isMember && (
                <Fragment>
                  {needsPurchaseCredits && (
                    <ConfirmAction
                      action={this.goToCredit}
                      description={formatMessage('You do not have enough credit to add new team member. Do you like to purchase credits?')}
                    >
                      {addMemberButton()}
                    </ConfirmAction>
                  )}
                  {!needsPurchaseCredits && (
                    addMemberButton(this.onCreateNewOrg)
                  )}
                </Fragment>
              )
            }
          </div>
          <div className="limit-wrapper">
            {formatMessage('Limit:')}{' '}{globals.teamMemberLimit}{' '}<br />
            {globals.teamMemberLimit <= currentMembers.length && (
              <span>
                {formatMessage('Credits are required to add additional team member:')}{' '}{toCurrency(globals.teamMemberFee)}
              </span>
            )}
          </div>
        </div>
        <div name="members-list" className="col-12">
          <Paper
            indicator
            loading={membersRequesting}
          >
            <List style={{ padding: 0 }}>
              {filteredMembers.length || membersRequesting ?
                filteredMembers.map(memberData => (
                  <OrganizationLoginMemberListItem
                    info={memberData}
                    isMember={isMember}
                    key={memberData.id}
                    roleMenuItems={roleMenuItems}
                    reinviteMember={reinviteMember}
                    searchText={searchText}
                    onDelete={() => this.handleDelete(memberData)}
                    updateItem={this.updateItem}
                  />
                )) :
                <div
                  style={{ fontSize: 24, color: 'rgba(0, 0, 0, 0.6)' }}
                  className="text-center p-5">
                  {formatMessage('You don\'t have any members in your organization')}
                </div>
              }
            </List>
          </Paper>
        </div>
        <OrganizationMemberDialog
          createItem={this.createItem}
          info={currentMember}
          isEdit={!!currentMember}
          onClose={this.handleClose}
          open={open}
          permissions={permissions}
          submitButtonTitle={organizationMemberDialogSubmitButtonTitle}
          title={organizationMemberDialogTitle}
          updateItem={this.updateItem}
        />
      </Fragment>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = state => ({
  ...selectState('members', 'members')(state, 'members'),
  ...selectState('options', 'permissions')(state, 'permissions'),
  ...selectState('buyers', 'buyers')(state, 'buyers'),
  ...selectState('sellers', 'sellers')(state, 'sellers'),
});

const mapDispatchToProps = dispatch => ({
  ...dispatchRestMaker([
    [memberActions, 'member'],
    [buyerActions, 'buyer'],
    [sellerActions, 'seller'],
  ])(dispatch),
  reinviteMember: id => dispatch(reinviteMember(id)),
});

export default compose(
  setDisplayName('Members'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStyles(styles),
  immutableToJS,
  injectIntl,
)(Members);
